import { SITE_URL } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, ArrowRight, Download, CalendarDays, ChevronDown } from "lucide-react";
import coursecatPhotoSrc from "@/assets/coursecat.png";
const coursecatPhoto = { url: coursecatPhotoSrc };
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { CatalogFeaturedCarousel } from "@/components/site/CatalogFeaturedCarousel";
import {
  catalogCourses as courses,
  featuredCourses,
  courseBehaviorNames,
  featuredSlugsForBehavior,
  CATALOG_PDF_URL,
  CATALOG_PDF_FILENAME,
} from "@/content/courses";
import {
  courseProblems,
  coursesForProblem,
  coursesForProblemText,
} from "@/content/course-problems";

export const Route = createFileRoute("/course-catalog/")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/course-catalog/` }],
    meta: [
      { title: "Course Catalog | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Browse the Leadership Edge Live course catalog: live, facilitator-led courses that develop the everyday behaviors of confident, consistent leaders.",
      },
      { property: "og:title", content: "Course Catalog | Leadership Edge Live" },
      {
        property: "og:description",
        content:
          "Live, facilitator-led leadership courses aligned to the 16 Leadership Edge Live Leader Behaviors.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CourseCatalog,
});

function CourseCatalog() {
  const [query, setQuery] = useState("");
  const [behavior, setBehavior] = useState("all");
  const [showAllForBehavior, setShowAllForBehavior] = useState(false);
  const [problem, setProblem] = useState("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    // Start from the mapped problem when one is picked, otherwise the full list.
    let list =
      problem === "all"
        ? [...courses].sort((a, b) => a.title.localeCompare(b.title))
        : coursesForProblem(problem).filter((c) => courses.some((x) => x.slug === c.slug));

    if (q) {
      const relevance = coursesForProblemText(q);
      const rank = new Map(relevance.map((c, i) => [c.slug, i]));
      list = list.filter((c) => (c.title.toLowerCase().includes(q) || rank.has(c.slug)));
      if (problem === "all") {
        list = [...list].sort((a, b) => {
          const aTitle = a.title.toLowerCase().includes(q) ? 0 : 1;
          const bTitle = b.title.toLowerCase().includes(q) ? 0 : 1;
          if (aTitle !== bTitle) return aTitle - bTitle;
          return (rank.get(a.slug) ?? 999) - (rank.get(b.slug) ?? 999);
        });
      }
    }

    if (behavior === "all") return list;
    list = list.filter((c) => (c.behaviors ?? []).includes(behavior));
    if (showAllForBehavior) return list;
    const picks = featuredSlugsForBehavior(behavior);
    return list.filter((c) => picks.has(c.slug));
  }, [query, behavior, problem, showAllForBehavior]);

  const behaviorTotal = useMemo(
    () => (behavior === "all" ? 0 : courses.filter((c) => (c.behaviors ?? []).includes(behavior)).length),
    [behavior],
  );

  const selectClass =
    "w-full appearance-none rounded-full border border-border/60 bg-background py-2.5 pl-4 pr-10 text-sm text-ink shadow-sm outline-none transition-colors focus:border-primary/40 focus:ring-2 focus:ring-primary/15";

  return (
    <>
      <section className="relative isolate overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={coursecatPhoto.url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="relative mx-auto flex min-h-[620px] max-w-6xl items-center px-6 py-16 md:py-20">
          <div className="max-w-[600px]">
            <h1
              className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Live courses that build everyday leader behavior
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Short, facilitator-led courses your managers can apply the same week.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/course-catalog/"
                className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
              >
                View the Course Catalog
              </Link>
              <Link
                to="/contact/"
                className="inline-flex items-center justify-center rounded-[6px] border border-black/40 bg-transparent px-6 py-3 text-sm font-semibold text-black transition-colors hover:border-black/60 hover:bg-black/5"
              >
                Talk to a Development Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden bg-background pt-14 pb-8 md:pt-28 md:pb-12">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            title="Featured courses"
            titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
            titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
          />
        </div>
        <CatalogFeaturedCarousel courses={featuredCourses} />
      </section>

      <Section tone="lavender" paddingClassName="pt-10 pb-14 md:pt-16 md:pb-28">
        <SectionHeader
          eyebrow="All Courses"
          title="The full catalog"
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
          intro="Every course maps to one or more of the 16 Leadership Edge Live Leader Behaviors."
        />

        <div className="mb-8 flex flex-col gap-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative w-full md:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search courses or describe a problem"
                aria-label="Search courses or describe a problem"
                className="w-full rounded-full border border-border/60 bg-background py-3 pl-10 pr-4 text-base text-ink sm:text-sm shadow-sm outline-none transition-colors focus:border-primary/40 focus:ring-2 focus:ring-primary/15"
              />
            </div>
            <div className="relative">
              <select
                value={behavior}
                onChange={(e) => {
                  setBehavior(e.target.value);
                  setShowAllForBehavior(false);
                }}
                aria-label="Filter by Leader Behavior"
                className={selectClass}
              >
                <option value="all">All Leader Behaviors</option>
                {courseBehaviorNames.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
            <div className="relative w-full md:max-w-sm">
              <select
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                aria-label="Filter by the problem you are trying to fix"
                className={selectClass}
              >
                <option value="all">Any problem</option>
                {courseProblems.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>
          {behavior !== "all" && (
            <p className="text-sm text-muted-foreground">
              {showAllForBehavior
                ? `Showing all ${behaviorTotal} courses for ${behavior}.`
                : `Showing featured courses for ${behavior}.`}{" "}
              <button
                type="button"
                onClick={() => setShowAllForBehavior((v) => !v)}
                className="font-semibold text-primary underline-offset-2 hover:underline"
              >
                {showAllForBehavior ? "Show featured only" : `See all ${behaviorTotal} courses`}
              </button>
            </p>
          )}
          {problem !== "all" && (
            <p className="text-sm text-muted-foreground">
              Showing the courses that help most with that problem, best matches first.{" "}
              <button
                type="button"
                onClick={() => setProblem("all")}
                className="font-semibold text-primary underline-offset-2 hover:underline"
              >
                Clear
              </button>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {filtered.length} course{filtered.length === 1 ? "" : "s"}
            </span>
            <a
              href={CATALOG_PDF_URL}
              download={CATALOG_PDF_FILENAME}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[6px] border border-primary/25 bg-background px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-lavender"
            >
              <Download className="h-3.5 w-3.5" />
              Download the course catalog (PDF)
            </a>
            <Link
              to="/schedule/"
              className="inline-flex items-center gap-1.5 rounded-[6px] border border-primary/25 bg-background px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-lavender"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              View entire schedule
            </Link>
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border/70 bg-background/60 p-10 text-center text-sm text-muted-foreground">
            No courses match those filters. Try a different keyword.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => (
              <Link
                key={c.slug}
                to="/course-catalog/$slug/"
                params={{ slug: c.slug }}
                className="group flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-border/60 bg-background transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)]"
              >
                <div className="aspect-square w-full overflow-hidden bg-lavender">
                  {c.image ? (
                    <img
                      src={c.image}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="h-full w-full gradient-brand opacity-80" />
                  )}
                </div>
                <div className="px-6 pt-5">
                  <h3 className="font-display text-base leading-snug text-ink transition-colors group-hover:text-primary">
                    {c.title}
                  </h3>
                </div>
                <span className="mt-4 inline-flex items-center gap-1.5 px-6 pb-6 text-xs font-semibold uppercase tracking-wider text-primary">
                  View course
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <AdvisorCTA
        title="Not sure where to start?"
        body="A Development Pro can match courses to the roles and behaviors worth developing first."
        backgroundImage={boardroomAsset.url}
      />
    </>
  );
}
